import { useEffect, useState, useCallback } from "react";
import { Lifebuoy, Plus, BookOpen, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Shared";
import { MultiImageField } from "@/components/MultiImageField";
import { AuthImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const PRIORITIES = ["low", "normal", "high", "blocking"];
const label = (v) => String(v || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const emptyTicket = { subject: "", module: "other", body: "", priority: "normal", images: [] };
const emptyArticle = { title: "", module: "other", summary: "", body: "", order: 0, published: true };

export default function Help() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("articles");
  const [modules, setModules] = useState([]);
  const [articles, setArticles] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [dialog, setDialog] = useState(null);
  const [ticket, setTicket] = useState(emptyTicket);
  const [article, setArticle] = useState(emptyArticle);
  const [reply, setReply] = useState({ ticket: null, body: "", close: false });
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (moduleFilter !== "all") params.set("module", moduleFilter);
      if (search) params.set("q", search);
      const [a, t] = await Promise.all([
        api.get(`/support/articles?${params.toString()}`),
        api.get("/support/tickets"),
      ]);
      setArticles(a.data);
      setTickets(t.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [moduleFilter, search]);

  useEffect(() => { api.get("/support/modules").then(({ data }) => setModules(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="help-page">
      <PageHeader
        overline="Help"
        title="Help & support"
        description="How-to guides for every part of the platform, and a direct line to the team when something is not right."
        action={
          <div className="flex flex-wrap gap-2">
            {isAdmin && (
              <Button variant="outline" data-testid="write-article-button" onClick={() => { setArticle(emptyArticle); setDialog("article"); }}>
                Write an article
              </Button>
            )}
            <Button data-testid="get-help-button" onClick={() => { setTicket(emptyTicket); setDialog("ticket"); }}>
              <Plus size={16} className="mr-1.5" /> Get help
            </Button>
          </div>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-4 min-w-0">
        <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
          <TabsTrigger value="articles" data-testid="help-tab-articles" className="rounded-none text-xs uppercase tracking-wider">
            <BookOpen size={14} className="mr-1.5" /> Help centre ({articles.length})
          </TabsTrigger>
          <TabsTrigger value="tickets" data-testid="help-tab-tickets" className="rounded-none text-xs uppercase tracking-wider">
            <Lifebuoy size={14} className="mr-1.5" /> {isAdmin ? "Ticket inbox" : "My tickets"} ({tickets.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "articles" && (
        <div data-testid="articles-panel">
          <div className="mb-4 grid gap-2 sm:grid-cols-[1fr_220px]">
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="article-search-input" placeholder="Search the help centre…" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none border-slate-300 pl-9" />
            </div>
            <Select value={moduleFilter} onValueChange={setModuleFilter}>
              <SelectTrigger data-testid="article-module-filter" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">Everything</SelectItem>
                {modules.map((m) => <SelectItem key={m} value={m}>{label(m)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {articles.length === 0 ? (
            <EmptyState testid="no-articles" title="Nothing found" description="Try a different word, or raise a ticket and we will help directly." />
          ) : (
            <div className="space-y-2" data-testid="article-list">
              {articles.map((a) => (
                <div key={a.id} data-testid={`article-${a.id}`} className="border border-border bg-white">
                  <button
                    type="button"
                    data-testid={`article-toggle-${a.id}`}
                    onClick={() => setOpen(open === a.id ? null : a.id)}
                    className="flex w-full items-start justify-between gap-3 p-4 text-left transition-colors duration-150 hover:bg-slate-50"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-[#0F172A]">{a.title}</span>
                      <span className="mt-1 block text-xs text-slate-500">{a.summary}</span>
                    </span>
                    <span className="overline shrink-0 text-[0.6rem] text-[#002FA7]">{label(a.module)}</span>
                  </button>
                  {open === a.id && (
                    <div data-testid={`article-body-${a.id}`} className="whitespace-pre-line border-t border-border p-4 text-sm leading-relaxed text-slate-700">
                      {a.body}
                      {isAdmin && (
                        <button type="button" data-testid={`delete-article-${a.id}`} onClick={() => run(() => api.delete(`/support/articles/${a.id}`), "Article removed")} className="mt-3 block text-xs text-[#B3241B] hover:underline">
                          Remove this article
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "tickets" && (
        <div data-testid="tickets-panel">
          {tickets.length === 0 ? (
            <EmptyState testid="no-tickets" title="No tickets" description="Press Get help and tell us what happened — a screenshot helps." />
          ) : (
            <div className="space-y-3">
              {tickets.map((t) => (
                <div key={t.id} data-testid={`ticket-${t.id}`} className="border border-border bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#0F172A]">{t.subject}</div>
                      <div className="text-xs text-slate-500">
                        {t.reference} · {label(t.module)} · {label(t.priority)}
                        {isAdmin ? ` · ${t.user_name} (${t.user_role})` : ""}
                      </div>
                    </div>
                    <StatusBadge value={t.status} testid={`ticket-status-${t.id}`} />
                  </div>
                  <p className="mt-2 whitespace-pre-line text-xs text-slate-600">{t.body}</p>
                  {(t.image_ids || []).length > 0 && (
                    <div className="mt-3 grid gap-2 sm:grid-cols-3" data-testid={`ticket-shots-${t.id}`}>
                      {t.image_ids.map((id) => (
                        <AuthImage
                          key={id}
                          imageId={id}
                          alt="Ticket screenshot"
                          className="h-28 w-full border border-border object-cover"
                          testid={`ticket-shot-${t.id}-${id}`}
                        />
                      ))}
                    </div>
                  )}
                  {(t.replies || []).map((r, i) => (
                    <div key={i} data-testid={`reply-${t.id}-${i}`} className={`mt-2 whitespace-pre-line border p-3 text-xs ${r.is_admin ? "border-[#CBD9FF] bg-[#EEF3FF] text-[#0F172A]" : "border-border text-slate-600"}`}>
                      <span className="font-semibold">{r.by}{r.is_admin ? " · support" : ""}</span>
                      <div className="mt-1">{r.body}</div>
                    </div>
                  ))}
                  {t.status !== "closed" && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" data-testid={`reply-btn-${t.id}`} onClick={() => { setReply({ ticket: t, body: "", close: false }); setDialog("reply"); }}>
                        Reply
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`close-ticket-${t.id}`} onClick={() => run(() => api.post(`/support/tickets/${t.id}/close`), "Ticket closed")}>
                        Close
                      </Button>
                    </div>
                  )}
                  {t.status === "closed" && (
                    <p data-testid={`ticket-closed-note-${t.id}`} className="mt-3 text-xs text-slate-500">
                      This ticket is closed. Press Get help to raise a new one if it comes back.
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Raise a ticket */}
      <Dialog open={dialog === "ticket"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Get help</DialogTitle>
            <DialogDescription>Tell us what happened and attach a screenshot — an admin is notified straight away.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Subject</Label><Input data-testid="ticket-subject-input" value={ticket.subject} onChange={(e) => setTicket({ ...ticket, subject: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div>
              <Label>Which part of the platform</Label>
              <Select value={ticket.module} onValueChange={(v) => setTicket({ ...ticket, module: v })}>
                <SelectTrigger data-testid="ticket-module-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{modules.map((m) => <SelectItem key={m} value={m}>{label(m)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>How urgent</Label>
              <Select value={ticket.priority} onValueChange={(v) => setTicket({ ...ticket, priority: v })}>
                <SelectTrigger data-testid="ticket-priority-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{label(p)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>What happened</Label><Textarea data-testid="ticket-body-input" rows={5} value={ticket.body} onChange={(e) => setTicket({ ...ticket, body: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2">
              <MultiImageField testid="ticket-shots" images={ticket.images} max={5} onChange={(images) => setTicket((t) => ({ ...t, images }))} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-ticket-button" disabled={busy || !ticket.subject} onClick={() => run(async () => {
              await api.post("/support/tickets", {
                subject: ticket.subject, module: ticket.module, body: ticket.body,
                priority: ticket.priority, page_url: window.location.href,
                image_ids: ticket.images.map((i) => i.image_id),
              });
              setDialog(null);
              setTab("tickets");
            }, "Ticket raised — we will get back to you")}>
              Send ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reply */}
      <Dialog open={dialog === "reply"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reply</DialogTitle><DialogDescription>{reply.ticket?.reference}</DialogDescription></DialogHeader>
          <Textarea data-testid="reply-body-input" rows={5} value={reply.body} onChange={(e) => setReply({ ...reply, body: e.target.value })} className="rounded-none border-slate-300" />
          {isAdmin && (
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid="reply-close-checkbox" checked={reply.close} onChange={(e) => setReply({ ...reply, close: e.target.checked })} />
              Close the ticket with this reply
            </label>
          )}
          <DialogFooter>
            <Button data-testid="submit-reply-button" disabled={busy || reply.body.trim().length < 2} onClick={() => run(async () => {
              await api.post(`/support/tickets/${reply.ticket.id}/replies`, { body: reply.body, close: reply.close });
              setDialog(null);
            }, "Reply sent")}>
              Send reply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Article */}
      <Dialog open={dialog === "article"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader><DialogTitle>Write a help article</DialogTitle><DialogDescription>Shown in the help centre for that part of the platform.</DialogDescription></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Title</Label><Input data-testid="article-title-input" value={article.title} onChange={(e) => setArticle({ ...article, title: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div>
              <Label>Module</Label>
              <Select value={article.module} onValueChange={(v) => setArticle({ ...article, module: v })}>
                <SelectTrigger data-testid="article-module-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{modules.map((m) => <SelectItem key={m} value={m}>{label(m)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Order</Label><Input data-testid="article-order-input" type="number" value={article.order} onChange={(e) => setArticle({ ...article, order: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2"><Label>One-line summary</Label><Input data-testid="article-summary-input" value={article.summary} onChange={(e) => setArticle({ ...article, summary: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2"><Label>The article</Label><Textarea data-testid="article-body-input" rows={7} value={article.body} onChange={(e) => setArticle({ ...article, body: e.target.value })} className="rounded-none border-slate-300" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-article-button" disabled={busy || !article.title} onClick={() => run(async () => {
              await api.post("/support/articles", { ...article, order: Number(article.order) || 0 });
              setDialog(null);
            }, "Article published")}>
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
